import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ElementRef,
  ViewChild,
} from "@angular/core";
import { IEvent } from "../../services/match.service";
import { MapBaseComponent } from "../../common/map-base/map-base.component";
import { ContextService } from "../../services/context.service";
import { StatsService } from "../../services/stats.service";
import * as L from "leaflet";
import gridGeom from "../../../assets/data/grid_4326.json";
import { jenks } from "./jenks";
import { ISelectedPlayer } from "../players-list/players-list.component";

@Component({
  selector: "app-map-grid",
  templateUrl: "./map-grid.component.html",
  styleUrls: [
    "../../common/map-base/map-base.component.scss",
    "./map-grid.component.scss",
  ],
})
export class MapGridComponent
  extends MapBaseComponent
  implements OnInit, OnDestroy
{
  private GRID_STYLE_NORMAL = {
    weight: 0,
    color: "#000000",
    fillColor: "#FFFFFF",
    fillOpacity: 0.5,
  };
  private GRID_STYLE_HOVER = {
    fillColor: "#383D4C",
    fillOpacity: 0.05,
  };

  _events: IEvent[] = [];
  _teams: any = {};
  _playersSelected: ISelectedPlayer[] = [];

  gridIndex = {};

  pointsLayerGroup: L.LayerGroup;
  squaresLayerGroup: L.LayerGroup;
  circlesLayerGroup: L.LayerGroup;

  squaresMap = {};
  circlesMap = {};
  firstPaint = true;
  popupData: any = {};
  playerSelectedMap = { home: null, visitor: null };

  private MAX_RADIUS = 0.45;
  private MIN_RADIUS = 0.2;

  @ViewChild("map", { static: true }) mapElem: ElementRef;

  @Input() get teams(): any {
    return this._teams;
  }
  set teams(teams: any) {
    this._teams = teams;
    this.refreshMap();
  }
  @Input() get events(): IEvent[] {
    return this._events;
  }
  set events(events: IEvent[]) {
    this._events = events;
    this.refreshMap();
  }
  @Input() get playersSelected(): ISelectedPlayer[] {
    return this._playersSelected;
  }
  set playersSelected(playersSelected: ISelectedPlayer[]) {
    this._playersSelected = playersSelected.filter(Boolean);
    this.refreshMap();
  }
  @Input() playersIndex = {};

  constructor(context: ContextService, statsService: StatsService) {
    super(statsService, context);
  }

  ngOnInit() {
    super.ngOnInit();
    this.refreshMap();
  }

  refreshMap() {
    if (this.map && this._events.length && this.teams.home) {
      this.gridIndex = this.statsService.calculateGrid(
        this.events,
        this.teams,
        this.context.getQuarter(),
        this.playersSelected
      );
      this.playerSelectedMap = this.getPlayerSelectedMap();
      const gridRotated = this.rotateGeomIfNeeded(gridGeom);
      if (this.firstPaint) {
        this.drawGrid(gridRotated);
        this.drawCircles();
        this.drawPoints();
        this.firstPaint = false;
      } else {
        this.clearCircles();
        this.drawCircles();
      }
    }
  }

  drawGrid(gridGeo) {
    this.squaresLayerGroup = L.geoJSON(gridGeo, {
      style: this.GRID_STYLE_NORMAL,
      onEachFeature: (feat, layer) => {
        this.squaresMap[feat.properties.id] = layer;
        layer.on({
          mouseover: (ev) => this.onLayerMouseover(ev),
          mouseout: (ev) => this.onLayerMouseout(ev),
        });
      },
    }).addTo(this.map);
  }

  drawPoints() {
    this.pointsLayerGroup = L.layerGroup().addTo(this.map);
    this.squaresLayerGroup.eachLayer((layer) => {
      const center = layer.getCenter();
      const pointLayer = L.circleMarker(center, {
        fillColor: "#AEB1B9",
        opacity: 0,
        fillOpacity: 1,
        weight: 1,
        radius: 1,
      });
      this.pointsLayerGroup.addLayer(pointLayer);
    });
    this.pointsLayerGroup.setZIndex(999);
  }

  drawCircles() {
    const imageUrl = "/assets/img/transparent.png";
    const shotArray = Object.keys(this.gridIndex).map(
      (key) => this.gridIndex[key].shots
    );
    this.circlesLayerGroup = L.layerGroup().addTo(this.map);
    const jenksClasses = Math.min(4, shotArray.length - 1);
    const jenksLimit =
      shotArray.length > 0 ? jenks(shotArray, jenksClasses) : [];

    this.squaresLayerGroup.eachLayer((layer) => {
      const grid_id = layer.feature.properties.id;
      const grid_data = this.gridIndex[grid_id];
      if (!grid_data) {
        return;
      }
      const center = layer.getCenter();
      const circleLayer = L.circle(center, {
        fill: false,
        color: "white",
        weight: 0,
        opacity: 0,
        fillOpacity: 0,
        radius: this.calculateCircleRadius(layer.feature, jenksLimit),
      });
      this.circlesLayerGroup.addLayer(circleLayer);
      const bounds = circleLayer.getBounds();

      const accuracy = this.statsService.calculateAccuracy(grid_data);
      const accuracyClass = this.getAccuracyColorClass(accuracy);
      const radius = this.calculateCircleRadius(layer.feature, jenksLimit);
      const className = `grid-circle color-${accuracyClass} radius-${radius}`;
      const imageLayer = L.imageOverlay(imageUrl, bounds, {
        className,
        zIndex: 200,
      });
      this.circlesMap[grid_id] = imageLayer;
      imageLayer.feature = layer.feature;
      this.circlesLayerGroup.addLayer(imageLayer);
    });
  }

  private getAccuracyColorClass(accuracy) {
    const step = 100 / 5;
    let zone;
    if (accuracy === 0) {
      zone = 0;
    } else if (accuracy < step) {
      zone = 1;
    } else if (accuracy < step * 2) {
      zone = 2;
    } else if (accuracy < step * 3) {
      zone = 3;
    } else if (accuracy < step * 4) {
      zone = 4;
    } else {
      zone = 5;
    }
    return zone;
  }

  calculateCircleRadius(feat, jenksLimit) {
    const data = this.gridIndex[feat.properties.id];
    if (!data) {
      return 0;
    }
    let radius = 0;
    const t0 = 0.2;
    const t1 = 0.25;
    const t2 = 0.3;
    const t3 = 0.4;
    const t4 = 0.45;
    const [l1, l2, l3, l4] = jenksLimit;
    if (data.shots > l4) {
      radius = t4;
    } else if (data.shots > l3) {
      radius = t3;
    } else if (data.shots > l2) {
      radius = t2;
    } else if (data.shots > l1) {
      radius = t1;
    } else {
      radius = t0;
    }
    return radius;
  }

  getPlayerSelectedMap() {
    return this.playersSelected.reduce(
      (map, p) => {
        const player = this.playersIndex[p.id];
        if (player.team_id === this._teams.home.id) {
          map.home = p.id;
        }
        if (player.team_id === this._teams.visitor.id) {
          map.visitor = p.id;
        }
        return map;
      },
      { home: null, visitor: null }
    );
  }

  onLayerMouseover(ev) {
    if (!ev.target.feature) {
      return;
    }
    const grid_id = ev.target.feature.properties.id;
    const squareLayer = this.squaresMap[grid_id];
    if (squareLayer) {
      squareLayer.setStyle(this.GRID_STYLE_HOVER);
    }
    const grid_data = this.gridIndex[grid_id] || {};
    const player_id =
      grid_data.team === "home"
        ? this.playerSelectedMap.home
        : this.playerSelectedMap.visitor;
    this.popupData = this.getPopupData(
      ev.target.feature,
      this.playersIndex[player_id]
    );
    this.openPopup(ev, "_path");
  }

  onLayerMouseout(ev) {
    if (!ev.target.feature) {
      return;
    }
    const grid_id = ev.target.feature.properties.id;
    const squareLayer = this.squaresMap[grid_id];
    if (squareLayer) {
      squareLayer.setStyle(this.GRID_STYLE_NORMAL);
    }
    this.closePopup(ev);
  }

  getPopupData(feat, player) {
    const data = this.gridIndex[feat.properties.id];
    if (!data) {
      return { empty: true };
    }
    const color = this.statsService.getAccuracyColor(
      this.gridIndex[feat.properties.id]
    );
    const accuracy = this.statsService.calculateAccuracy(data);
    const team = this.teams[data.team];
    return {
      ...data,
      color,
      accuracy,
      team: team ? team.name : "",
      player,
    };
  }

  clearCircles() {
    this.circlesMap = {};
    if (this.circlesLayerGroup) {
      this.circlesLayerGroup.remove();
      delete this.circlesLayerGroup;
    }
  }

  ngOnDestroy() {
    this.clearCircles();
    this.squaresMap = {};
    this.firstPaint = true;
    if (this.pointsLayerGroup) {
      this.pointsLayerGroup.remove();
      delete this.pointsLayerGroup;
    }
    if (this.squaresLayerGroup) {
      this.squaresLayerGroup.remove();
      delete this.squaresLayerGroup;
    }
    super.ngOnDestroy();
  }
}
